import { useEffect, useState } from 'react'
import {
  ApiError, avlusingFor, fishState, penCounts, withdrawLiceCount,
  type AvlusingSvar, type LiceCount, type LiceDetail, type LiceTreatment,
  type FishState, type Me, type Pen, type PenCount,
} from '../api'
import { invalidate, useApi } from '../useApi'
import { Link } from '../router'
import { ErrorBox, Rows, placeName } from '../components/bits'
import { Menu } from '../components/Menu'
import { PageHead } from '../components/Shell'
import { Varsel, dagMnd, isoUke, nb } from '../components/Varsel'
import { Merder } from '../components/Merder'
import { Anleggsvisning } from '../components/Anleggsvisning'
import { Merdtelling } from '../components/Merdtelling'
import { Merdhistorikk } from '../components/Merdhistorikk'
import { Fisken, type Ark } from '../components/Fisken'
import { Syklus } from '../components/Syklus'
import { PerTiFisk } from '../components/PerTiFisk'
import { Telling, datoLang, metodeNavn } from '../components/Telling'
import { useT } from '../lang'

/* One farm, as the people who run it see it.
 *
 * The staff view of a site is an editor: name, coordinates, operator, the full
 * deployment history. This is not that, and since 2026-08-11 it is not a
 * summary of the farm either — it is the lice forecast and the one weekly act
 * that keeps the forecast worth reading.
 *
 * WHY LISTENING LEFT THIS PAGE. The register publishes a monthly window weeks
 * behind, so on any given Thursday its figure is the wrong number to decide a
 * treatment on and the farm's own count is the right one. Entering that count
 * is a scheduled, regulated, weekly job; listening to a hydrophone is
 * occasional. Making the occasional thing the primary button and burying the
 * weekly one inside a card was backwards. Listening is reached from the farm
 * card on the front page until the player gets a home of its own.
 */

interface FarmDetail {
  site_id: string
  name: string
  municipality: string | null
  locality_no: number | null
  /** What this farm has bought. The Log link follows it, so a customer is not
   *  shown a door that answers 403. */
  features?: string[]
}

/** `v0` is the panel as it shipped before the decision shape existed, kept
 *  whole and reachable at its own URL so the two can be looked at side by side
 *  on the same farm and the same data. Deleting it to make room for the new one
 *  would have made the comparison an argument instead of an observation. */
export function Farm({ id, me }: {
  id: string; me: Me
}) {
  const t = useT()
  const q = useApi<FarmDetail>(`/v1/sites/${encodeURIComponent(id)}`)
  // Lifted out of LicePanel so the page's own CTA can open the form. The form
  // renders directly under the head, above the strip: what you are about to
  // change stays visible below what you are typing, and on a phone the fields
  // are where your thumb already is.
  const [adding, setAdding] = useState<string | null>(null)
  /* The merd grid, and which ring it should open on. Null means closed; a
     record with a null pen means "opened from the button rather than from a
     ring on the map". */
  const [telling, setTelling] = useState<{ pen: string | null } | null>(null)

  /* WHICH COUNT FORM THE BUTTON OPENS, decided by whether this farm has rings.
   *
   * A farm whose merder are registered gets the grid: every ring on one screen,
   * one pass, which is what has to beat their spreadsheet. A farm without them
   * gets the site-level wizard, exactly as every farm did until the rings
   * existed. So the migration is per farm and needs no flag: drawing a farm's
   * merder is what moves it, and nothing has to be switched over.
   *
   * A 403 here means the lice product was not bought, `items` stays undefined,
   * and the old button is the right one to show. */
  const pens = useApi<{ items: Pen[] }>(
    `/v1/sites/${encodeURIComponent(id)}/pens`)
  const harMerder = (pens.data?.items.length ?? 0) > 0

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  const site = q.data
  if (!site) return null

  return (
    <>
      <PageHead
        title={site.name}
        lead={[placeName(site.municipality),
               site.locality_no ? `${t('Locality')} ${site.locality_no}` : null]
          .filter(Boolean).join(' · ')}
        cta={me.can?.lice_count !== false && (harMerder ? (
          <button className="btn primary"
            onClick={() => setTelling(telling ? null : { pen: null })}>
            {telling ? t('Cancel') : t('Register a count')}
          </button>
        ) : (
          <button className="btn primary"
            onClick={() => setAdding(adding === null ? today() : null)}>
            {adding === null ? t('Add a lice count') : t('Cancel')}
          </button>
        ))}
      />

      <LicePanel siteId={id} me={me} adding={adding} setAdding={setAdding}
        telling={telling} setTelling={setTelling}
        pens={pens.data?.items ?? []} />

      {/* One quiet line back to the sound, at the bottom where a secondary
          route belongs. Listening left this page so that the weekly job could
          have the accent; it did not stop existing, and a farmer who arrives
          here from the card should not have to go back to reach it. */}
      {(me.can?.listen !== false || me.can?.lice_book === true) && (
        <p className="farm-foot">
          {me.can?.listen !== false && (
            <Link className="btn ghost sm"
              to={`/sites/${encodeURIComponent(id)}/lytt`}>
              {t('Listen')}
            </Link>
          )}
          {/* The printed book belongs to this page's product. Its rail entry
              is desktop-only (the phone's bar is full), so without this a
              farmer on a phone -- most of them -- has no way in. */}
          {me.can?.lice_book === true && (
            <Link className="btn ghost sm"
              to={`/sites/${encodeURIComponent(id)}/lusedagbok`}>
              Lusedagbok
            </Link>
          )}
          {/* One quiet route in, not two. The log has a rail entry of its own
              now -- gated on the purchase -- and repeating it here would be a
              second door to the same room on a page whose job is the weekly
              lice count. Listening keeps its link because it predates the rail
              entry and people know where it is. */}
        </p>
      )}
    </>
  )
}

/* ------------------------------------------------------------------- lice */

const today = () => new Date().toISOString().slice(0, 10)


function LicePanel({ siteId, me, adding, setAdding, telling, setTelling,
                    pens }: {
  siteId: string; me: Me
  adding: string | null
  setAdding: (d: string | null) => void
  telling: { pen: string | null } | null
  setTelling: (v: { pen: string | null } | null) => void
  /** Fetched once in Farm, because the CTA above already needs to know whether
   *  this farm has rings before it can decide which count form to open. */
  pens: Pen[]
}) {
  const t = useT()
  /* ONE PAGE, AND THE TABS ARE GONE.
   *
   * This was Kart / Varsel / Historikk, and the argument for the split was
   * that they are three different claims from three different sources, so the
   * seam between them is real. The seam IS real — but it is a seam in our data
   * sources, not in the farmer's question, and nobody standing on a walkway
   * has ever thought "I would like the backward horizon now".
   *
   * The proof that they were one thing was on the screen the whole time: this
   * week appeared under Varsel as a row and under Historikk as a row, drawn
   * two different ways, in two colour systems, with two vocabularies. One
   * fact rendered twice, and a reader who noticed would be right to trust
   * neither.
   *
   * They are one timeline. The forecast is the same series continued by a
   * model fitted to the history; the only thing that changes at today is that
   * the uncertainty opens up. So the ribbon draws both halves on one axis, the
   * map sits on top of it, and the tables that used to be two tabs are the
   * appendix underneath — open when somebody needs the exact figure for week
   * 32, which is a real need and a rare one.
   *
   * THE WEEK BEING SHOWN. Null is now. Set by dragging the ribbon, which
   * redraws the map with it: scrub back six months and the farm replays, rings
   * recoloured and fish standing where they stood. */
  const [tilbake, setTilbake] = useState<string | null>(null)
  /* THE APPENDIX IS NOT MOUNTED UNTIL IT IS ASKED FOR.
   *
   * `<details>` keeps its children in the DOM whether it is open or not, so
   * simply putting the tables inside one would have `Merdhistorikk` fetch the
   * farm's whole pen-history on every page load — a second copy of the request
   * `Syklus` is already making, for a panel nobody has opened. Two components
   * fetching the same URL is the thing this page's own comment warns about
   * eight lines down.
   *
   * Held in React rather than left to the element so that the panel stays open
   * across a rerun: somebody who opened it to check a figure, then saved a
   * count, should not have to open it again to see the result. */
  const [tallene, setTallene] = useState(false)
  /* One ring, selected. Shared by the map and the table beneath it so that
     clicking a polygon highlights a row and clicking a row highlights a
     polygon — they are two views of one set of rows and should not be able to
     disagree about which one you are looking at. */
  const [valgtMerd, setValgtMerd] = useState<string | null>(null)
  /* THE FISH, AND A PROPOSED MOVE. Held here because the gesture happens on the
     MAP and the sheet that resolves it renders in the panel below — two
     components, one act, so the state belongs to whoever holds both. */
  const [fisk, setFisk] = useState<FishState | null>(null)
  const [ark, setArk] = useState<Ark | null>(null)
  /** A move picked up by TAPPING a badge rather than dragging it. */
  const [armFrom, setArmFrom] = useState<string | null>(null)
  const path = `/v1/sites/${encodeURIComponent(siteId)}/lice`
  const q = useApi<LiceDetail>(path)
  const [rerunning, setRerunning] = useState(false)
  const [rerunFailed, setRerunFailed] = useState(false)
  // The delousing what-if. Deliberately NOT in `useApi`: it is not part of what
  // the page is, it is a question the reader asked, and it should vanish when
  // they stop asking it rather than persist in a cache keyed by URL.
  const [baatuke, setBaatuke] = useState('')
  const [avlusing, setAvlusing] = useState<AvlusingSvar | null>(null)
  const [avBusy, setAvBusy] = useState(false)
  const [avFeil, setAvFeil] = useState<string | null>(null)

  async function velgBaatuke(v: string | null) {
    setBaatuke(v ?? '')
    setAvFeil(null)
    // The previous answer STAYS while the next one loads, dimmed. Clearing it
    // would collapse the column and reopen it two or three seconds later, and
    // a table that flinches every time you move the boat is harder to compare
    // across than one that holds still. Only letting go of the boat empties it.
    if (!v) setAvlusing(null)
    if (!v) return
    const [aar, uke] = v.split('-W')
    setAvBusy(true)
    try {
      const svar = await avlusingFor(siteId, Number(aar), Number(uke))
      // `available: false` is an ordinary answer here, not a failure: a site
      // with no count before the boat in its current cycle cannot be answered
      // for, and the model says which of its refusals it is.
      if (svar.available) setAvlusing(svar)
      else setAvFeil(svar.reason ?? null)
    } catch (e) {
      setAvFeil(e instanceof ApiError ? e.message : String(e))
    } finally {
      setAvBusy(false)
    }
  }

  /* THE DAY'S RINGS, FETCHED ONCE HERE. The map and the table below it draw
     the same rows, and two components fetching the same URL is two components
     that disagree for a second after every write — the ring turns green on the
     map while the row under it still reads the old number. */
  const dag = q.data?.own_counts?.[0]?.counted_at ?? null
  /** The week on screen. `dag` until somebody drags the ribbon. */
  const viser = tilbake ?? dag
  const historisk = tilbake !== null && tilbake !== dag

  const [merdrader, setMerdrader] = useState<PenCount[] | null>(null)
  useEffect(() => {
    if (!viser) { setMerdrader(null); return }
    let dead = false
    void penCounts(siteId, viser)
      .then((r) => { if (!dead) setMerdrader(r.items) })
      // Not an error box. A farm with no rings registered answers with an
      // empty list, and a farm whose lice product was never bought answers
      // 403 — in both cases the page below is the whole product and is fine.
      .catch(() => { if (!dead) setMerdrader([]) })
    return () => { dead = true }
  }, [siteId, viser])

  /* WHO WAS STANDING WHERE THAT WEEK. Fetched only while the playhead is back,
     because `Fisken` already holds the present and asking twice for the same
     answer is two components that disagree for a second after every write. */
  const [fortid, setFortid] = useState<FishState | null>(null)
  useEffect(() => {
    if (!historisk || !tilbake) { setFortid(null); return }
    let dead = false
    void fishState(siteId, tilbake)
      .then((r) => { if (!dead) setFortid(r) })
      .catch(() => { if (!dead) setFortid(null) })
    return () => { dead = true }
  }, [siteId, tilbake, historisk])

  if (q.cold) return <Rows n={3} />
  // A 403 here means the product was not bought. That is not an error and it is
  // not a page to apologise on; the panel simply is not there.
  if (q.error?.code === 'not_entitled') return null
  if (q.error) return <ErrorBox error={q.error} />
  const d = q.data
  if (!d) return null

  if (!d.available) {
    return (
      <div className="card">
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('Lice')}</h2>
        <p className="muted" style={{ margin: 0 }}>{d.reason}</p>
      </div>
    )
  }

  const cur = d.current!
  /** The limit in force the week being shown — never "the limit now". Scrub
   *  back into April and the rings must be measured against April's. */
  const viserGrense = viser ? grenseFor(d, viser) : null
  const v = d.varsel && d.varsel.available !== false ? d.varsel : null
  const counts = d.own_counts ?? []
  const origo = v?.meta?.origo

  /* Saving the count and rebuilding the forecast are two different things that
     can fail separately, and before this they looked like one. The POST returns
     the stored counts and the verdict, so the count is applied at once; the
     forecast is a second request whose failure says only that the forecast is
     stale, never that the count was lost. */
  const rerun = () => {
    setRerunning(true)
    setRerunFailed(false)
    invalidate(path)
    void (async () => {
      const ok = await q.reloadAsync()
      setRerunFailed(!ok)
      setRerunning(false)
      /* The delousing answer was computed from the origin that has just moved,
         so leaving it on screen would put a fresh forecast beside a stale
         counterfactual and invite exactly the comparison neither number
         supports. The QUESTION survives -- entering a count does not make
         "what if we delouse in week 36" a worse question -- so the boat stays
         where they put it and only the answer is fetched again.

         After the reload rather than beside it: the model holds two request
         slots, and racing the forecast against the delousing for them makes
         both slower for no gain. */
      if (ok && baatuke) await velgBaatuke(baatuke)
    })()
  }

  /* The overlay stays open on its own last screen and closes itself. All this
     does is get the page underneath ready, so the strip has already redrawn by
     the time they press through. */
  const saved = (items: LiceCount[]) => {
    q.patch((prev) => (prev ? { ...prev, own_counts: items } : prev))
    rerun()
  }

  return (
    <>
      {adding !== null && (
        <Telling siteId={siteId} initialDate={adding}
          counts={counts} treatments={d.treatments ?? []}
          windowEnd={d.window_end ?? null}
          origin={origo?.dato ?? null}
          grense={grenseFor(d, adding)} tempHint={tempHint(d)}
          onSaved={saved} onClose={() => setAdding(null)} />
      )}

      {telling !== null && (
        <Merdtelling siteId={siteId} focusPen={telling.pen}
          initialDate={today()}
          onSaved={(items) => { setMerdrader(items); rerun() }}
          onClose={() => setTelling(null)} />
      )}

      <div className="card stack" style={{ gap: 18 }}>
        {/* THE ANSWER, FIRST, AS SOMETHING COUNTABLE.
          *
          * "0,42" is a mean of a right-skewed distribution written as a
          * fraction of an animal, and there is no such thing as four tenths of
          * a louse. Everybody here can recite the figure and almost nobody can
          * picture it. Four lice among ten fish, against a rule at five, is the
          * same fact in a form a person can check by looking. See the
          * component — the rule it follows is Neurath's, and it is older than
          * every dashboard this industry has been shown. */}
        <PerTiFisk cur={cur} />

        {rerunFailed && (
          <div className="notice">
            <strong>{t('Your count is saved.')}</strong>{' '}
            {t('The forecast could not be fetched just now.')}
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn ghost sm" onClick={rerun}>
                {t('Try again')}
              </button>
            </div>
          </div>
        )}

        {/* ---------------------------------------------- the array, this week */}
        {counts.length > 0 && (
          <Anleggsvisning siteId={siteId} counts={merdrader ?? []}
            /* The farm's own figure for the week being shown, so a farm that
               has never counted per merd shows its rings coloured by what it
               DOES know rather than as eight empty circles. Painted dashed and
               captioned; see the component header. */
            siteValue={viser
              ? (counts.find((c) => c.counted_at === viser)?.adult_female
                 ?? counts[0]!.adult_female) : null}
            grense={viserGrense}
            selected={valgtMerd} onSelect={setValgtMerd}
            occupancy={(historisk ? fortid : fisk)?.occupancy}
            /* NOTHING IS WRITTEN FROM A WEEK IN THE PAST. Dragging a badge on
               a map showing March would record a move today, out of a ring the
               fish are not standing in — and the picture would look exactly
               right while it did. So the gestures are simply absent while the
               playhead is back, and the notice below says why. */
            onMoveFish={me.can?.lice_count !== false && !historisk
              ? (from, to) => setArk({ maal: 'pen', from, to })
              : undefined}
            /* Dropped on the tray rather than on a ring. `kind` is what
               the tray element declared, and the sheet takes it from
               there. */
            onDropTarget={me.can?.lice_count !== false && !historisk
              ? (from, kind) =>
                setArk({ maal: kind as Ark['maal'], from, to: from })
              : undefined}
            onArmFish={me.can?.lice_count !== false && !historisk
              ? (pen) => setArmFrom(armFrom === pen ? null : pen)
              : undefined}
            armedFrom={armFrom}
            /* Names only once there are two populations to tell apart. */
            showNames={new Set(((historisk ? fortid : fisk)?.occupancy ?? [])
              .map((o) => o.group_id)).size > 1}
            /* The newest move on record, so a badge that has just arrived
               swims there rather than being deleted from one ring and
               built in another. Ignored on the first paint; see the
               component. */
            sisteFlytt={historisk ? null : (fisk?.moves?.[0] ?? null)}
            /* The room the card cannot give it. A shuffle happens on the
               big one. */
            fullskjermHref={`/sites/${encodeURIComponent(siteId)}/map`}
            setupHref={me.is_staff !== false
              ? `/sites/${encodeURIComponent(siteId)}/oppsett` : undefined} />
        )}

        {/* THE ARC, UNDER THE ARRAY, AND IT IS THE WHOLE OF WHAT WAS THREE
            TABS. Weeks across, the limit as a rule through them, the record to
            the left of now and the model's interval to the right. Drag along
            it and the map replays. */}
        <Syklus siteId={siteId}
          grenseFor={(iso) => grenseFor(d, iso)}
          moves={fisk?.moves ?? []}
          groups={fisk?.groups ?? []}
          varsel={v ? (v.timeseries ?? []) : []}
          valgt={viser}
          onVelg={(day) => { setTilbake(day); setArmFrom(null) }} />

        {/* Said where the next tap would be, not where the scrubbing is.
            Somebody who has dragged back six months and then reaches for a
            fish has to meet this before they meet a dead badge. */}
        {historisk && (
          <div className="notice">
            <strong>{t('You are looking at an earlier week.')}</strong>{' '}
            {t('Nothing can be changed from here — a move recorded now would be filed on the wrong week.')}
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn sm" onClick={() => setTilbake(null)}>
                {t('Back to now')}
              </button>
            </div>
          </div>
        )}

        {/* The one act the map leads to. Placed under the picture and
            addressed to the ring already selected, so "merd 3 looks bad"
            and "count merd 3" are one gesture apart. */}
        {valgtMerd && me.can?.lice_count !== false && !historisk
          && telling === null && (
          <div>
            <button className="btn sm"
              onClick={() => setTelling({ pen: valgtMerd })}>
              {t('Count this pen')}
            </button>
          </div>
        )}

        {/* Which population is standing where, the register's offer when
            nothing has been said yet, and the sheet the map's drag opens.
            Under the picture, because the picture is where the gesture
            lives. */}
        {pens.length > 0 && !historisk && (
          <Fisken siteId={siteId} pens={pens}
            canWrite={me.can?.lice_count !== false}
            onChanged={setFisk} ark={ark} setArk={setArk}
            armFrom={armFrom} setArmFrom={setArmFrom}
            valgtMerd={valgtMerd} onPlaced={() => setValgtMerd(null)} />
        )}

        {/* THE RINGS, UNDER THE PICTURE OF THEM.
            The regulation is written per merd and the report is written per
            locality, so this is the evidence. Map and table are the same rows
            and sit together: the picture says which ring, the table says how
            much, and separating them would make the reader hold one in their
            head while looking at the other. */}
        {counts.length > 0 && (
          <Merder siteId={siteId} items={merdrader}
            grense={viserGrense}
            canWrite={me.can?.lice_count !== false && !historisk}
            onItems={setMerdrader}
            selected={valgtMerd} onSelect={setValgtMerd}
            onCount={(pen) => setTelling({ pen })}
            onChanged={rerun} />
        )}

        {/* Asked where the need is actually felt: under a ribbon whose right
          * half is built on an origin this says out loud the age of. */}
        {v && adding === null && telling === null && !historisk
          && me.can?.lice_count !== false
          && (origo?.ukerSidenTelling ?? 0) >= 2 && (
          <div className="telt-siden">
            <p style={{ margin: 0 }}>
              <strong>{t('Have you counted since?')}</strong>{' '}
              {t('The forecast is built on')}{' '}
              {origo?.kilde === 'telling'
                ? t('a count from')
                : t("the register's figure from")}{' '}
              {origo?.dato ? datoLang(origo.dato) : ''}.
            </p>
            <button className="btn sm" onClick={() => setTelling({ pen: null })}>
              {t('Register a count')}
            </button>
          </div>
        )}

        {/* ------------------------------------------------------ the record

            THE TABLES ARE THE APPENDIX NOW, NOT THE PAGE.

            The week table and the forecast strip are still the finer record
            and they still have to exist: the exact figure for merd 3 in week
            32 is what goes to Mattilsynet, and a ribbon is worse than a table
            at reading one cell out of a hundred. That is a real loss and this
            is where it is paid back.

            What they are NOT is the first thing anybody meets. Above this line
            is one picture of one farm on one axis; below it is the evidence,
            open when somebody has a reason to check a number rather than read
            a shape. Closed by default, and it stays closed across a rerun —
            nothing under here is mounted until it is opened — see `tallene`
            above for why that is not just a nicety. */}
        <details className="tallene" open={tallene}
          onToggle={(e) => setTallene(e.currentTarget.open)}>
          <summary>{t('Show the figures')}</summary>

          {tallene && (
          <div className="stack" style={{ gap: 18, marginTop: 14 }}>
            {v ? (
              <Varsel varsel={v} symbols={d.symbols} counts={counts}
                register={d.register_weeks} current={cur} rerunning={rerunning}
                treatments={d.treatments ?? []}
                avlusing={avlusing} avlusingLaster={avBusy}
                valgtUke={d.avlusing_available ? baatuke : null}
                onVelgUke={d.avlusing_available && adding === null
                  ? velgBaatuke : undefined} />
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                {t('No forecast for this farm.')}
              </p>
            )}

            {/* Only when the model refused. The question is asked on the table
              * itself, so there is nothing to render here in the ordinary
              * case. */}
            {avFeil && <p className="varsel-fot faint">{avFeil}</p>}

            {/* Weeks down, merder across. One week says which ring is worst;
                a year says whether it is the same ring every time, and that is
                a mooring decision rather than a delousing one. */}
            <Merdhistorikk siteId={siteId}
              grenseFor={(iso) => grenseFor(d, iso)}
              canWrite={me.can?.lice_count !== false}
              onChanged={rerun} />

            {counts.length > 0 ? (
              <Counts items={counts} siteId={siteId}
                canWrite={me.can?.lice_count !== false}
                onCorrect={setAdding} onChanged={rerun} />
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                {t('Nothing counted here yet.')}
              </p>
            )}

            {(d.treatments ?? []).length > 0 && (
              <Treatments items={d.treatments!} />
            )}

            <Provenance detail={d} />
          </div>
          )}
        </details>
      </div>
    </>
  )
}

/** A starting figure for the sea temperature.
 *
 *  Their own last reading first. Sea temperature at a cage moves by well under
 *  a degree a week, so last Tuesday's measurement at this exact farm is a
 *  better prior than anything gridded — and it costs nothing, because the
 *  counts are already on the page.
 *
 *  Failing that, the register's own in-situ mean over its report window. A
 *  month old and for this locality, which still beats an empty field.
 *
 *  Deliberately not the SST climatology, which is also available: for Ljøsøy in
 *  August it says 15,6 against the register's 12,0 and the model's current
 *  11,4. A default should not be the outlier of the three.
 */
function tempHint(d: LiceDetail): { value: number; kilde: string } | null {
  const own = (d.own_counts ?? []).find((c) => c.temp_c != null)
  if (own?.temp_c != null) {
    return { value: own.temp_c, kilde: `Fra tellingen deres ${dagMnd(own.counted_at)}` }
  }
  if (d.sea_temperature_mean_c != null) {
    return { value: Math.round(d.sea_temperature_mean_c * 10) / 10,
             kilde: 'Registerets snitt for forrige periode' }
  }
  return null
}

/** The limit in force for the week a given date falls in. Read per week, never
 *  once: 0,50 normally, 0,20 in weeks 16–21, 0,10 there in a red production
 *  area — invisible today and wrong in April. */
function grenseFor(d: LiceDetail, iso: string): number | null {
  const want = isoUke(iso)
  for (const w of d.varsel?.timeseries ?? []) {
    if (w.data.uken && isoUke(w.time) === want) return w.data.uken.details.grense
  }
  return d.current?.threshold ?? null
}

/* THE VERDICT AS A WORD IS GONE WITH THE CHIP THAT CARRIED IT.
 *
 * `Over grensen` / `Nærmer seg grensen` / `Under grensen` was a fourth
 * rendering of one number, on a page that already drew it as signs against a
 * rule, as a line against a rule, and as a decimal. A word cannot say how far
 * over, and "nærmer seg" cannot say how close — both are the reader's own
 * question and both are answered by looking at where the mark sits. */


/* ------------------------------------------------------------- the record */

/** The verdict is kept as COLOUR and dropped as a word.
 *
 *  "innenfor" was the whole cell, and landing inside an eighty per cent interval
 *  is what should happen four times in five -- so it was close to a non-claim
 *  taking up the one column that could have carried a number. `Telling.tsx`
 *  already reached this conclusion for the overlay; this is the table catching
 *  up with it.
 *
 *  `voided` keeps its word, because there is nothing to compare: the farm
 *  deloused between the forecast's origin and the count, so the forecast
 *  described a world that did not happen. A number there would read as a miss. */
const VERDICT: Record<string, { cls: string; label: string }> = {
  inside: { cls: 'ok', label: 'innenfor' },
  above: { cls: 'bad', label: 'over' },
  below: { cls: 'warn', label: 'under' },
  voided: { cls: '', label: 'avluset' },
}

/** What the forecast said about this week, computed before the count existed —
 *  a hindcast, not a re-read: the receiver asks the model with every count
 *  EXCEPT this one, so the origin is genuinely where it was at the time.
 *
 *  The horizon, the origin and the head that answered go in the tooltip rather
 *  than the cell. The head matters more than it looks: v2's centre was the
 *  median and v2b's is the mean, so two rows from either side of 18 August are
 *  not the same quantity, and a column that averaged them would be measuring
 *  nothing. Stated per row because that is where the difference lives. */
function Varslet({ check }: { check: NonNullable<LiceCount['check']> }) {
  const v = check.verdict ? VERDICT[check.verdict] : null
  if (check.verdict === 'voided') {
    return <span className="chip">{v?.label ?? 'avluset'}</span>
  }
  const tittel = [
    `${check.weeks_ahead} uke${check.weeks_ahead === 1 ? '' : 'r'} fram`,
    check.origin_date
      ? `fra ${check.origin_kind === 'telling' ? 'telling' : 'register'} `
        + check.origin_date
      : null,
    check.symbol,
    check.model,
  ].filter(Boolean).join(' · ')
  return (
    <span className={`varslet ${v?.cls ?? ''}`} title={tittel}>
      {nb(check.central)}
      {check.p10 != null && check.p90 != null && (
        <span className="varslet-omr">
          {' '}{nb(check.p10)}–{nb(check.p90)}
        </span>
      )}
    </span>
  )
}

function Counts({ items, siteId, canWrite, onCorrect, onChanged }: {
  items: LiceCount[]; siteId: string; canWrite: boolean
  onCorrect: (date: string) => void
  onChanged: () => void
}) {
  const t = useT()
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const withdraw = async (countId: string) => {
    setBusy(countId)
    setErr(null)
    try {
      await withdrawLiceCount(siteId, countId)
      onChanged()
    } catch (e) {
      setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : 'Kunne ikke trekke tilbake tellingen.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 14 }}>{t('Your counts')}</h3>
      {err && <div className="notice bad">{err}</div>}
      <table className="tbl tellinger">
        <thead>
          <tr>
            <th>{t('Week')}</th><th>{t('Counted')}</th>
            <th>{t('Adult female')}</th>
            <th>{t('Mobile')}</th><th>{t('Sessile')}</th>
            <th>{t('Fish')}</th><th>°C</th><th>{t('Forecast')}</th>
            <th>{t('By')}</th><th />
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 12).map((c) => {
            return (
              <tr key={c.count_id}>
                {/* `data-l` carries the column heading down to the cell. On a
                    phone the heading row is gone and each cell states its own
                    name -- without it the row is a column of naked numbers and
                    0,50, 1,90 and 1,20 are indistinguishable. */}
                <td className="faint t-uke" data-l={t('Week')}>
                  {isoUke(c.counted_at)}
                </td>
                <td className="t-dato">{dagMnd(c.counted_at)}</td>
                <td className="num t-vh" data-l={t('Adult female')}>
                  {c.adult_female != null ? nb(c.adult_female) : '—'}
                </td>
                <td className="num t-bev" data-l={t('Mobile')}>
                  {c.mobile != null ? nb(c.mobile) : '—'}
                </td>
                <td className="num t-fast" data-l={t('Sessile')}>
                  {c.sessile != null ? nb(c.sessile) : '—'}
                </td>
                <td className="faint num t-fisk" data-l={t('Fish')}>
                  {c.n_fish ?? '—'}
                </td>
                <td className="faint num t-temp" data-l="°C">
                  {c.temp_c != null ? nb(c.temp_c, 1) : '—'}
                </td>
                <td className="num t-varsel" data-l={t('Forecast')}>
                  {c.check?.verdict
                    ? <Varslet check={c.check} />
                    : <span className="faint">—</span>}
                </td>
                <td className="faint t-av" title={c.note ?? undefined}>
                  {c.counted_by ?? '—'}
                  {c.note && <span className="notat"> · {c.note}</span>}
                </td>
                <td className="t-meny" style={{ textAlign: 'right' }}>
                  {canWrite && (
                    <Menu label={`${t('Count')} ${c.counted_at}`}
                      busy={busy === c.count_id}
                      actions={[
                        { label: t('Correct it'),
                          onSelect: () => onCorrect(c.counted_at) },
                        { label: t('Withdraw it'),
                          confirm: t('Withdraw it?'),
                          detail: t('It stops counting, stops anchoring the model, and frees that day again. The record that it was entered is kept.'),
                          danger: true,
                          onSelect: () => void withdraw(c.count_id) },
                      ]} />
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Treatments({ items }: { items: LiceTreatment[] }) {
  const t = useT()
  return (
    <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>
      {t('Delousings on record')}:{' '}
      {items.slice(0, 4).map((x, i) => (
        <span key={x.treatment_id}>
          {i > 0 && ' · '}
          {datoLang(x.treated_at)} ({metodeNavn(x.method).toLowerCase()}
          {x.coverage < 1 && `, ${Math.round(x.coverage * 100)} %`})
        </span>
      ))}
    </p>
  )
}

/** Where every number on this card came from, and how old it is. A lice figure
 *  that looks current and is three weeks old is worse than none. */
function Provenance({ detail }: { detail: LiceDetail }) {
  const t = useT()
  const f = detail.forecast
  const parts: string[] = []
  if (detail.window_start && detail.window_end) {
    parts.push(`${t('Register window')} ${detail.window_start} – ${detail.window_end}`)
  }
  const v = detail.varsel
  if (v?.meta) {
    parts.push(`${t('Forecast anchored on')} ${v.meta.origo.dato}`)
    parts.push(`${t('Model')} ${v.meta.modell} ${v.meta.versjon}`)
    if (v.meta.origo.ukerSidenTelling > 1) {
      parts.push(`${v.meta.origo.ukerSidenTelling} ${t('weeks since the last count reached the model')}`)
    }
  }
  if (!v && f?.somAv) parts.push(`${t('Forecast anchored on')} ${f.somAv}`)
  if (detail.forecast_source === 'none') parts.push(t('No forecast: showing reported figures only'))
  if (f?.stale) parts.push(t('the model was unreachable, this is the last answer'))
  if (!parts.length) return null
  return (
    <p className="faint" style={{ margin: 0, fontSize: 12 }}>{parts.join(' · ')}</p>
  )
}
